import { Fragment, useEffect, useRef, useState } from "react";
import {
  ShieldCheck,
  FileText,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/format";
import {
  useAdminPixVerificationDocumentUrlLazyQuery,
  useAdminPixVerificationsQuery,
  useAdminReviewPixVerificationMutation,
} from "@/generated/graphql";

type StatusFilter = "pending" | "verified" | "rejected" | "all";
type ReviewedStatus = "verified" | "rejected";
type ReviewOverride = {
  status: ReviewedStatus;
  reason: string | null;
};

const PAGE_SIZE = 25;

const STATUS_FILTERS: readonly StatusFilter[] = [
  "pending",
  "verified",
  "rejected",
  "all",
];

const STATUS_LABEL: Record<string, string> = {
  unverified: "Não enviado",
  pending: "Em análise",
  verified: "Verificado",
  rejected: "Reprovado",
};

const COL_SPAN = 6;

type DocumentSide = "front" | "back";

function getSafeDocumentUrl(value: string | null | undefined) {
  if (!value) return null;

  try {
    return new URL(value).protocol === "https:" ? value : null;
  } catch {
    return null;
  }
}

// The presigned read URL baked into the list response has a 5-minute TTL, so it
// is already dead by the time an admin working down the queue reaches this row.
// Mint a fresh one on click instead, and never render the stale one as an href.
function DocumentButton({
  id,
  value,
  side,
  label = "ver arquivo",
  onOpen,
}: {
  id: string;
  value: string | null | undefined;
  side?: DocumentSide;
  label?: string;
  onOpen: (id: string, side?: DocumentSide) => Promise<void>;
}) {
  const [opening, setOpening] = useState(false);

  if (!getSafeDocumentUrl(value)) {
    return <span className="text-sm text-muted-foreground">indisponível</span>;
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={opening}
      className="gap-1.5"
      onClick={() => {
        setOpening(true);
        void onOpen(id, side).finally(() => setOpening(false));
      }}
    >
      {opening ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <FileText className="w-3.5 h-3.5" />
      )}
      {opening ? "abrindo..." : label}
    </Button>
  );
}

export default function AdminPixVerifications() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [openInline, setOpenInline] = useState<Record<string, "reject" | null>>({});
  const [rejectReason, setRejectReason] = useState<Record<string, string>>({});
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reviewOverrides, setReviewOverrides] = useState<Record<string, ReviewOverride>>({});
  const reviewInFlightRef = useRef(false);
  const reviewedIdsRef = useRef(new Set<string>());

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [search]);

  const { data, loading, error, refetch } = useAdminPixVerificationsQuery({
    variables: {
      status: statusFilter,
      search: debouncedSearch || null,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    fetchPolicy: "cache-and-network",
  });
  const [reviewVerification] = useAdminReviewPixVerificationMutation();
  const [fetchDocumentUrl] = useAdminPixVerificationDocumentUrlLazyQuery({
    // Always hit the network: a cached URL is a URL that has had time to expire.
    fetchPolicy: "network-only",
  });

  const handleOpenDocument = async (id: string, side?: DocumentSide) => {
    // Open the tab synchronously with the click — popup blockers reject a
    // window.open that happens after an await.
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;

    try {
      const result = await fetchDocumentUrl({ variables: side ? { id, side } : { id } });
      const documentUrl = getSafeDocumentUrl(
        result?.data?.adminPixVerificationDocumentUrl,
      );

      if (!documentUrl) {
        tab?.close();
        setActionError("Não foi possível abrir o documento.");
        return;
      }

      setActionError(null);
      if (tab) {
        tab.location.href = documentUrl;
      } else {
        window.location.href = documentUrl;
      }
    } catch (openError) {
      tab?.close();
      setActionError(
        openError instanceof Error
          ? openError.message
          : "Não foi possível abrir o documento.",
      );
    }
  };

  const serverRows = data?.adminPixVerifications.data ?? [];
  const total = data?.adminPixVerifications.total ?? 0;
  const pageCount = data?.adminPixVerifications.pageCount ?? 0;

  useEffect(() => {
    if (!loading && page > Math.max(1, pageCount)) {
      setPage(Math.max(1, pageCount));
    }
  }, [loading, page, pageCount]);

  useEffect(() => {
    if (loading || error || !data) return;

    const latestRows = data.adminPixVerifications.data;
    setReviewOverrides((currentOverrides) => {
      let changed = false;
      const nextOverrides = { ...currentOverrides };

      for (const [id, override] of Object.entries(currentOverrides)) {
        const serverRow = latestRows.find((row) => row.id === id);
        if (serverRow?.status === override.status) {
          delete nextOverrides[id];
          changed = true;
        }
      }

      return changed ? nextOverrides : currentOverrides;
    });
  }, [data, error, loading, reviewOverrides]);

  const rows = serverRows
    .map((row) => {
      const override = reviewOverrides[row.id];
      return override
        ? { ...row, status: override.status, review_reason: override.reason }
        : row;
    })
    .filter((row) => !(reviewedIdsRef.current.has(row.id) && row.status === "pending"))
    .filter((row) => statusFilter === "all" || row.status === statusFilter);

  const handleReview = async (
    id: string,
    status: ReviewedStatus,
    reason: string | null,
  ) => {
    if (reviewInFlightRef.current) return;

    reviewInFlightRef.current = true;
    setActionError(null);
    setReviewingId(id);

    try {
      await reviewVerification({
        variables: { id, status, reason },
      });
    } catch (reviewError) {
      setActionError(
        reviewError instanceof Error
          ? reviewError.message
          : "Não foi possível revisar a solicitação.",
      );
      reviewInFlightRef.current = false;
      setReviewingId(null);
      return;
    }

    setReviewOverrides((currentOverrides) => ({
      ...currentOverrides,
      [id]: { status, reason },
    }));
    reviewedIdsRef.current.add(id);

    if (status === "rejected") {
      setOpenInline((state) => ({ ...state, [id]: null }));
      setRejectReason((reasons) => ({ ...reasons, [id]: "" }));
    }

    try {
      await refetch();
    } catch {
      setActionError("Revisão salva, mas não foi possível atualizar a lista.");
    } finally {
      reviewInFlightRef.current = false;
      setReviewingId(null);
    }
  };

  const handleApprove = async (id: string) => {
    await handleReview(id, "verified", null);
  };

  const handleReject = async (id: string) => {
    const reason = rejectReason[id]?.trim();
    if (!reason) return;

    await handleReview(id, "rejected", reason);
  };

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-primary" />
              <div>
                <h1 className="text-xl font-bold">Verificação de PIX</h1>
                <p className="text-sm text-muted-foreground">
                  Fila de comprovantes (CPF + documento) enviados pelos clipadores
                </p>
              </div>
            </div>
            <Badge variant="outline" className="text-red-500 border-red-500">ADMIN</Badge>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle>Solicitações</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <div
                    className="flex items-center gap-1 border border-border rounded-md p-1"
                    role="group"
                    aria-label="Filtrar por status"
                  >
                    {STATUS_FILTERS.map((s) => (
                      <Button
                        key={s}
                        variant={statusFilter === s ? "default" : "ghost"}
                        size="sm"
                        className="h-7 px-2 text-xs"
                        aria-pressed={statusFilter === s}
                        onClick={() => {
                          setStatusFilter(s);
                          setPage(1);
                        }}
                      >
                        {s === "all" ? "Todos" : STATUS_LABEL[s]}
                      </Button>
                    ))}
                  </div>
                  <Input
                    aria-label="Buscar verificações"
                    placeholder="Buscar por nome, e-mail ou CPF"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    className="max-w-xs"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {actionError && (
                <div role="alert" className="mb-4 text-sm text-red-500">
                  {actionError}
                </div>
              )}
              {error && serverRows.length > 0 && !actionError && reviewingId === null && (
                <div role="alert" className="mb-4 text-sm text-amber-500">
                  Não foi possível atualizar a lista. Exibindo dados em cache.
                </div>
              )}
              {loading && rows.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  Carregando solicitações...
                </div>
              ) : error && serverRows.length === 0 ? (
                <div role="alert" className="text-center py-10 text-red-500">
                  {error.message || "Não foi possível carregar as solicitações."}
                </div>
              ) : rows.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <p className="font-medium">Nenhuma solicitação por aqui</p>
                  <p className="text-xs mt-1">
                    Nenhuma verificação corresponde aos filtros.
                  </p>
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Clipador</TableHead>
                        <TableHead>CPF</TableHead>
                        <TableHead>Documento</TableHead>
                        <TableHead>Enviado em</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[220px]">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r) => (
                        <Fragment key={r.id}>
                          <TableRow>
                            <TableCell>
                              <p className="font-medium text-sm">{r.user_name || "—"}</p>
                              <p className="text-xs text-muted-foreground">{r.user_email || "—"}</p>
                            </TableCell>
                            <TableCell className="text-sm font-mono">{r.cpf}</TableCell>
                            <TableCell>
                              {r.document_back_url ? (
                                // Front and back sent as separate photos.
                                <div className="flex flex-wrap gap-1.5">
                                  <DocumentButton
                                    id={r.id}
                                    value={r.document_url}
                                    side="front"
                                    label="ver frente"
                                    onOpen={handleOpenDocument}
                                  />
                                  <DocumentButton
                                    id={r.id}
                                    value={r.document_back_url}
                                    side="back"
                                    label="ver verso"
                                    onOpen={handleOpenDocument}
                                  />
                                </div>
                              ) : (
                                <DocumentButton
                                  id={r.id}
                                  value={r.document_url}
                                  onOpen={handleOpenDocument}
                                />
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                              {formatDateTime(r.submitted_at || r.createdAt)}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  r.status === "verified"
                                    ? "default"
                                    : r.status === "rejected"
                                      ? "destructive"
                                      : "outline"
                                }
                                className="text-[10px]"
                              >
                                {STATUS_LABEL[r.status] || r.status}
                              </Badge>
                              {r.review_reason && (
                                <p className="mt-1 text-xs text-muted-foreground">
                                  {r.review_reason}
                                </p>
                              )}
                            </TableCell>
                            <TableCell className="space-x-2 whitespace-nowrap">
                              {r.status === "pending" && (
                                <>
                                  <Button
                                    size="sm"
                                    className="gap-1.5"
                                    disabled={reviewingId !== null}
                                    onClick={() => void handleApprove(r.id)}
                                  >
                                    <Check className="w-3.5 h-3.5" />
                                    Aprovar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="gap-1.5"
                                    disabled={reviewingId !== null}
                                    onClick={() => setOpenInline((s) => ({ ...s, [r.id]: "reject" }))}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                    Reprovar
                                  </Button>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                          {openInline[r.id] === "reject" && (
                            <TableRow>
                              <TableCell colSpan={COL_SPAN}>
                                <div className="border border-red-500/30 rounded-lg p-3 space-y-2 bg-red-500/5">
                                  <Label
                                    htmlFor={`pix-rejection-reason-${r.id}`}
                                    className="text-xs"
                                  >
                                    Motivo da reprovação
                                  </Label>
                                  <Textarea
                                    id={`pix-rejection-reason-${r.id}`}
                                    disabled={reviewingId !== null}
                                    value={rejectReason[r.id] || ""}
                                    onChange={(e) =>
                                      setRejectReason((s) => ({ ...s, [r.id]: e.target.value }))
                                    }
                                    placeholder="Ex: foto ilegível, nome não confere com o CPF..."
                                    rows={2}
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      disabled={!rejectReason[r.id]?.trim() || reviewingId !== null}
                                      onClick={() => void handleReject(r.id)}
                                    >
                                      <X className="w-3 h-3 mr-1" />
                                      Confirmar reprovação
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      disabled={reviewingId !== null}
                                      onClick={() => setOpenInline((s) => ({ ...s, [r.id]: null }))}
                                    >
                                      Cancelar
                                    </Button>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="flex items-center justify-between gap-3 border-t px-3 py-3">
                    <div className="flex gap-2 text-sm text-muted-foreground">
                      <span>{total} solicitações</span>
                      <span>{page} / {pageCount}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                        disabled={page === 1}
                        aria-label="Página anterior"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((currentPage) => Math.min(pageCount, currentPage + 1))}
                        disabled={page >= pageCount}
                        data-testid="pix-verifications-next-page"
                        aria-label="Próxima página"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </Layout>
  );
}
